import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, MessageCircle, Send, Loader2, Facebook, Instagram, Youtube } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";

const Kontak = () => {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useSeo("Kontak", "Hubungi Madrasah Ibtidaiyah Futuhiyyah Doro, Pekalongan.");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Mohon lengkapi nama, email, dan pesan.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contacts", form);
      toast.success(data.message || "Pesan terkirim!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Gagal mengirim pesan. Coba lagi.");
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Alamat", value: settings.address },
    { icon: Phone, label: "Telepon", value: settings.phone },
    { icon: Mail, label: "Email", value: settings.email },
  ];

  return (
    <div>
      <PageHeader title="Hubungi Kami" subtitle="Kami senang mendengar dari Anda. Sampaikan pertanyaan atau pesan Anda." crumbs={[{ label: "Kontak" }]} />
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="grid gap-10 lg:grid-cols-2">
          {/* Info */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Informasi Kontak</h2>
            <div className="mt-6 space-y-4">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><it.icon className="h-5 w-5" /></div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{it.label}</p>
                    <p className="mt-1 text-slate-800">{it.value}</p>
                  </div>
                </div>
              ))}
              {settings.whatsapp && (
                <a href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"
                  className="flex items-center gap-4 rounded-2xl bg-emerald-600 p-5 text-white shadow-sm transition-colors hover:bg-emerald-700">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20"><MessageCircle className="h-5 w-5" /></div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-100">WhatsApp</p>
                    <p className="mt-1 font-medium">Chat Langsung dengan Kami</p>
                  </div>
                </a>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              {settings.facebook && <a href={settings.facebook} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 p-3 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"><Facebook className="h-4 w-4" /></a>}
              {settings.instagram && <a href={settings.instagram} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 p-3 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"><Instagram className="h-4 w-4" /></a>}
              {settings.youtube && <a href={settings.youtube} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 p-3 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700"><Youtube className="h-4 w-4" /></a>}
            </div>
          </div>

          {/* Form */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Kirim Pesan</h2>
            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="mt-6 space-y-4 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
              <div>
                <label className="text-sm font-medium text-slate-700">Nama</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" data-testid="contact-name-input" className="mt-1.5" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@contoh.com" data-testid="contact-email-input" className="mt-1.5" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Nomor HP</label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" data-testid="contact-phone-input" className="mt-1.5" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Pesan</label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tuliskan pesan Anda..." rows={5} data-testid="contact-message-input" className="mt-1.5" />
              </div>
              <Button type="submit" disabled={sending} className="w-full gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="contact-form-submit-button">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Mengirim..." : "Kirim Pesan"}
              </Button>
            </motion.form>
          </div>
        </div>

        {/* Maps */}
        <div className="mt-12 overflow-hidden rounded-2xl border border-slate-200/80 shadow-sm">
          <iframe
            title="Lokasi MI Futuhiyyah"
            src={settings.maps_embed}
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
};

export default Kontak;
